/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import React, {useEffect, useState } from "react";
import PropTypes from "prop-types";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import Topheader from "../../../Layout/header/header";
import {
  Autocomplete,
  Backdrop,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Radio,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import PopupDialog from "../../../Components/PopupDialog";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import { StyledHeaderCellTypo, StyledBodyCellTypo } from "../users/Users.jsx";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchGlobalAdminCategoriesDetails } from "../../../Services/redux/global-admin-users/categories/GlobalAdminCategoriesActions.js";
import APIManager from "../../../Services/ApiManager/ApiManager.js";
import GlobalAdminSidebar from "../../../Components/GlobalAdminSidebar/GlobalAdminSidebar.jsx";

const isNotNone = (domain) => domain !== "NONE";

const getDomain = (domainAliasIds) => {
  if (!domainAliasIds || domainAliasIds.length === 0) return "NONE";
  if (domainAliasIds.length === 1) return domainAliasIds[0];
  return domainAliasIds.find(isNotNone) || "NONE";
};

const getGroupModelNames = (group) => group.models.map((item) => item.modelName);

const compareCategoryNames = (order, a, b) => {
  const nameA = a.translatedNames?.["en-US"] || "";
  const nameB = b.translatedNames?.["en-US"] || "";
  return order === "asc" ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
};

const compareMmyNames = (order, a, b) => {
  const nameA = a.name || "";
  const nameB = b.name || "";
  return order === "asc" ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
};

const applyFiltersToData = (data, domainChecked, makeChecked, modelChecked) => {
  let filtered = data;
  if (domainChecked.length > 0) {
    filtered = filtered.filter((group) => domainChecked.includes(group.domain));
  }
  if (makeChecked.length > 0) {
    filtered = filtered.filter((group) => makeChecked.includes(group.manufacturerName));
  }
  if (modelChecked.length > 0) {
    filtered = filtered
      .map((group) => ({
        ...group,
        models: group.models.filter((model) => modelChecked.includes(model.modelName)),
      }))
      .filter((group) => group.models.length > 0);
  }
  return filtered;
};

const buildYearEntry = (manufacturer, model, year) => ({
  id: `${model.id}-${year}`,
  name: `${manufacturer.name} ${model.name} ${year}`,
  manufacturerName: manufacturer.name,
  manufacturerId: manufacturer.id,
  modelName: model.name,
  year,
  years: model.years,
  domain: getDomain(manufacturer.domainAliasIds),
  contentEditable: false,
});

const buildBaseEntry = (manufacturer, model) => ({
  id: model.id,
  name: `${manufacturer.name} ${model.name}`,
  manufacturerName: manufacturer.name,
  manufacturerId: manufacturer.id,
  modelName: model.name,
  years: model.years,
  domain: getDomain(manufacturer.domainAliasIds),
  contentEditable: true,
});

const buildModelEntries = (manufacturer, model) => [
  buildBaseEntry(manufacturer, model),
  ...model.years.map((year) => buildYearEntry(manufacturer, model, year)),
];

const buildManufacturerGroup = (manufacturer) => ({
  manufacturerId: manufacturer.id,
  manufacturerName: manufacturer.name,
  domain: getDomain(manufacturer.domainAliasIds),
  models: manufacturer.models.flatMap((model) => buildModelEntries(manufacturer, model)),
});

const computeReadOnly = (isDomainApprover) =>
  sessionStorage.getItem("readonly_GlobalAdmin") === "true" ||
  (isDomainApprover && sessionStorage.getItem("readonly_DomainApprover") === "true");

const getInitialSidebarItem = (isDomainApprover, locationState, initialSection) => {
  if (initialSection) return initialSection;
  if (isDomainApprover) return "MakeMyModel";
  return locationState?.selectedSection || "Categories";
};

const validateMMYForm = (makeModelForm, isDomainApprover) => {
  const isDomainValid = isDomainApprover ? true : makeModelForm.domain.trim() !== "";
  const isMakeValid = makeModelForm.make.trim() !== "";
  const isModelValid = makeModelForm.model.trim() !== "";
  const isYearValid = makeModelForm.year.length > 0;
  const allValid = isDomainValid && isMakeValid && isModelValid && isYearValid;
  return { isDomainValid, isMakeValid, isModelValid, isYearValid, allValid };
};

const buildSavePayload = (makeModelForm, makeMyModel, domains, isDomainApprover, domainDA, isEditMode, editingMMYData) => {
  const selectedManufacturer = makeMyModel.find(
    (group) => group.domain === makeModelForm.domain && group.manufacturerName === makeModelForm.make
  );
  const manufacturerId = selectedManufacturer ? selectedManufacturer.manufacturerId : null;
  const selectedDomain = domains.find((domain) => domain.domainAlias === makeModelForm.domain);
  const domainId = selectedDomain ? selectedDomain.domainAliasId : makeModelForm.domain;
  const payload = {
    make: makeModelForm.make,
    modelNames: [makeModelForm.model],
    years: makeModelForm.year.map(Number),
    domainAliasIds: isDomainApprover ? [domainDA] : [domainId],
  };
  const modelId = isEditMode && editingMMYData ? editingMMYData.id : null;
  return { manufacturerId, payload, modelId };
};

const getErrorHeader = (errorMessage) =>
  errorMessage === "jwt expired" ? "401 Token Expired" : "Error";

const SortIcon = ({ sortOrder }) =>
  sortOrder === "asc" ? (
    <ArrowDownwardIcon sx={{ fontSize: "18px", color: "#12141599" }} />
  ) : (
    <ArrowUpwardIcon sx={{ fontSize: "18px", color: "#12141599" }} />
  );

const MmyRowAction = ({ data, onEdit, readOnly }) =>
  data.contentEditable ? (
    <IconButton
      size="small"
      sx={{ padding: "4px", color: "#666666", "&:hover": { backgroundColor: "#E0E0E0" } }}
      onClick={() => onEdit(data)}
      disabled={readOnly}
    >
      <EditIcon sx={{ fontSize: 20 }} />
    </IconButton>
  ) : (
    <IconButton size="small" disabled sx={{ padding: "4px", color: "#CCCCCC", cursor: "not-allowed" }}>
      <DeleteIcon sx={{ fontSize: 20 }} />
    </IconButton>
  );

const toggleSortOrder = (prevOrder) => prevOrder === "asc" ? "desc" : "asc";

const toggleArrayItem = (arr, item) =>
  arr.includes(item) ? arr.filter((i) => i !== item) : [...arr, item];

const getAvailableMakesFromData = (makeMyModel, domainChecked) => {
  if (domainChecked.length === 0) {
    return [...new Set(makeMyModel.map((group) => group.manufacturerName))];
  }
  return [
    ...new Set(
      makeMyModel
        .filter((group) => domainChecked.includes(group.domain))
        .map((group) => group.manufacturerName)
    ),
  ];
};

const getAvailableModelsFromData = (makeMyModel, domainChecked, makeChecked) => {
  let filteredGroups = makeMyModel;
  if (domainChecked.length > 0) {
    filteredGroups = filteredGroups.filter((group) =>
      domainChecked.includes(group.domain)
    );
  }
  if (makeChecked.length > 0) {
    filteredGroups = filteredGroups.filter((group) =>
      makeChecked.includes(group.manufacturerName)
    );
  }
  return [
    ...new Set(
      filteredGroups.flatMap((group) =>
        group.models.map((item) => item.modelName)
      )
    ),
  ];
};

const getDisplayedData = (filteredData, allData) =>
  filteredData.length > 0 ? filteredData : allData;

const getSortedModels = (data, page, sortOrder) => {
  const models = data[page]?.models;
  return models
    ? [...models].sort((a, b) => compareMmyNames(sortOrder, a, b))
    : [];
};

const getEditFetchCall = (isDomainApprover) =>
  isDomainApprover ? APIManager.getmmyYears() : APIManager.getAdminDomain();

const renderConditional = (condition, element) => condition ? element : null;

const getContentMarginLeft = (isDomainApprover) => isDomainApprover ? "0px" : "320px";

const PaginationControls = ({ currentPage, totalPages, onPageChange }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
      marginTop: "16px",
      gap: 1,
    }}
  >
    <IconButton
      size="small"
      onClick={() => onPageChange(0)}
      disabled={currentPage === 0}
      sx={{
        color: currentPage === 0 ? "#CCCCCC" : "#666666",
        "&:hover": {
          backgroundColor: currentPage === 0 ? "transparent" : "#F5F5F5",
        },
      }}
    >
      <KeyboardDoubleArrowLeftIcon />
    </IconButton>
    <IconButton
      size="small"
      onClick={() => onPageChange(currentPage - 1)}
      disabled={currentPage === 0}
      sx={{
        color: currentPage === 0 ? "#CCCCCC" : "#666666",
        "&:hover": {
          backgroundColor: currentPage === 0 ? "transparent" : "#F5F5F5",
        },
      }}
    >
      <KeyboardArrowLeftIcon />
    </IconButton>
    <IconButton
      size="small"
      onClick={() => onPageChange(currentPage + 1)}
      disabled={currentPage === totalPages - 1}
      sx={{
        color: currentPage === totalPages - 1 ? "#CCCCCC" : "#666666",
        "&:hover": {
          backgroundColor: currentPage === totalPages - 1 ? "transparent" : "#F5F5F5",
        },
      }}
    >
      <KeyboardArrowRightIcon />
    </IconButton>
    <IconButton
      size="small"
      onClick={() => onPageChange(totalPages - 1)}
      disabled={currentPage === totalPages - 1}
      sx={{
        color: currentPage === totalPages - 1 ? "#CCCCCC" : "#666666",
        "&:hover": {
          backgroundColor: currentPage === totalPages - 1 ? "transparent" : "#F5F5F5",
        },
      }}
    >
      <KeyboardDoubleArrowRightIcon />
    </IconButton>
  </Box>
);

const getItemsInDomain = (makeMyModel, domain) => {
  const groups = makeMyModel.filter((group) => group.domain === domain);
  const manufacturers = new Set(groups.map((group) => group.manufacturerName));
  const models = new Set(groups.flatMap(getGroupModelNames));
  return { manufacturers, models };
};

const computeDomainToggle = (prev, domain, makeMyModel, makeChecked, modelChecked, setMakeChecked, setModelChecked) => {
  const isRemoving = prev.includes(domain);
  if (!isRemoving) {
    return [...prev, domain];
  }
  const { manufacturers, models } = getItemsInDomain(makeMyModel, domain);
  setMakeChecked(makeChecked.filter((make) => !manufacturers.has(make)));
  setModelChecked(modelChecked.filter((model) => !models.has(model)));
  return prev.filter((item) => item !== domain);
};

const computeMakeToggle = (prev, make, makeMyModel, modelChecked, setModelChecked) => {
  const isRemoving = prev.includes(make);
  if (!isRemoving) {
    return [...prev, make];
  }
  const modelsInMake = new Set(
    makeMyModel
      .filter((group) => group.manufacturerName === make)
      .flatMap(getGroupModelNames)
  );
  setModelChecked(modelChecked.filter((model) => !modelsInMake.has(model)));
  return prev.filter((item) => item !== make);
};

const getAddCategoryPath = (isDomainApprover) =>
  isDomainApprover ? "/ui/domain-approver/categories/add-new-category" : "/ui/harman-admin/categories/add-new-category";

const getCategoryDetailPath = (isDomainApprover, id) =>
  isDomainApprover ? `/ui/domain-approver/categories/${id}` : `/ui/harman-admin/categories/${id}`;

const getCategoryIconSrc = (icon) => icon?.thumbnailUrl || icon?.url || "";

const shouldShowMakeMyModel = (isDomainApprover, selectedSidebarItem) =>
  (isDomainApprover && selectedSidebarItem !== "Categories") || selectedSidebarItem === "MakeMyModel";

const GlobalAdminCategories = ({ isDomainApprover = false, initialSection }) => {
  const readOnly_GlobalAdmin = computeReadOnly(isDomainApprover);
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
  const [makeDropdownOpen, setMakeDropdownOpen] = useState(false);
  const [domainDropdownOpen, setDomainDropdownOpen] = useState(false);
  const [domainChecked, setDomainChecked] = useState([]);
  const [makeChecked, setMakeChecked] = useState([]);
  const [modelChecked, setModelChecked] = useState([]);
  const [domainSearch, setDomainSearch] = useState("");
  const [domainDA, setDomainDA] = useState("");
  const [makeSearch, setMakeSearch] = useState("");
  const [modelSearch, setModelSearch] = useState("");
  const [addMakeModelModalOpen, setAddMakeModelModalOpen] = useState(false);
  const [makeModelForm, setMakeModelForm] = useState({
    make: "",
    model: "",
    year: [],
    domain: "",
  });
  const [makeInputValue, setMakeInputValue] = useState("");
  const [modelInputValue, setModelInputValue] = useState("");
  const [modelError, setModelError] = useState(false);
  const [domainError, setDomainError] = useState(false);
  const [makeError, setMakeError] = useState(false);
  const [yearError, setYearError] = useState(false);
  const [showDomainDropdown, setShowDomainDropdown] = useState(true);
  const [refreshData, setRefreshData] = useState(0);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingMMYData, setEditingMMYData] = useState(null);
  const [componentName, setComponentName] = useState("");
  const [openErrorPopup, setOpenErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [headerMessage, setHeaderMessage] = useState("");

  // Handler for Domain checkbox toggle
  const handleDomainToggle = (domain) => {
    setDomainChecked((prev) => computeDomainToggle(prev, domain, makeMyModel, makeChecked, modelChecked, setMakeChecked, setModelChecked));
  };

  // Handler for Make checkbox toggle
  const handleMakeToggle = (make) => {
    setMakeChecked((prev) => computeMakeToggle(prev, make, makeMyModel, modelChecked, setModelChecked));
  };

  // Handler for Model checkbox toggle
  const handleModelToggle = (model) => {
    setModelChecked((prev) => toggleArrayItem(prev, model));
  };

  // Handler for Reset to Default button
  const handleResetFilter = () => {
    setDomainChecked([]);
    setMakeChecked([]);
    setModelChecked([]);
    setDomainSearch("");
    setMakeSearch("");
    setModelSearch("");
    setFilteredMakeMyModel([]); // Clear filters
    setCurrentManufacturerPage(0);
  };

  // Handler for Cancel button
  const handleCancelFilter = () => {
    setFilterDrawerOpen(false);
  };

  // Handler for Apply button
  const handleApplyFilter = () => {
    setFilteredMakeMyModel(applyFiltersToData(makeMyModel, domainChecked, makeChecked, modelChecked));
    setCurrentManufacturerPage(0); // Reset to first page
    setFilterDrawerOpen(false);
  };
  const dispatch = useDispatch();
  const location = useLocation();

  const [loader, setLoader] = useState(false);
  const [selectedSidebarItem, setSelectedSidebarItem] = useState(
    getInitialSidebarItem(isDomainApprover, location.state, initialSection)
  );
  const navigate = useNavigate();
  const [makeMyModel, setMakeMyModel] = useState([]);
  const [filteredMakeMyModel, setFilteredMakeMyModel] = useState([]);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [makeMyModelDomain, setMakeMyModelDomain] = useState([]);
  const [domains, setDomains] = useState([]);
  const [currentManufacturerPage, setCurrentManufacturerPage] = useState(0);
  const [mmyYears, setMmyYears] = useState([]);
  const [categorySortOrder, setCategorySortOrder] = useState("asc");
  const [mmySortOrder, setMmySortOrder] = useState("asc");

  const globalCategories = useSelector(
    (state) => state.globalAdminCategories.categories
  );

  // Sorting handler for Categories table
  const handleCategorySortToggle = () => {
    setCategorySortOrder(toggleSortOrder);
  };

  // Sorting handler for Make Model Year table
  const handleMmySortToggle = () => {
    setMmySortOrder(toggleSortOrder);
  };

  // Sorted categories
  const sortedCategories = [...(globalCategories || [])].sort((a, b) =>
    compareCategoryNames(categorySortOrder, a, b)
  );

  const handleAddMMY = async () => {
    setIsEditMode(false);
    setEditingMMYData(null);
    setMakeModelForm({
      make: "",
      model: "",
      year: [],
      domain: "",
    });
    setMakeInputValue("");
    setModelInputValue("");
    setModelError(false);
    setDomainError(false);
    setMakeError(false);
    setYearError(false);
    setShowDomainDropdown(true);

    setAddMakeModelModalOpen(true);
    setAddMakeModelModalOpen(true);
    const years = await APIManager.getmmyYears()
      .then((data) => data)
      .catch((err) => {
        console.log(err);
      });
    setMmyYears(years);
  };

  const handleEditMMY = async (mmyData) => {
    setIsEditMode(true);
    setEditingMMYData(mmyData);
    setMakeModelForm({
      make: mmyData.manufacturerName || "",
      model: mmyData.modelName || "",
      year: mmyData.years || [],
      domain: mmyData.domain || "",
    });
    setMakeInputValue(mmyData.manufacturerName || "");
    setModelInputValue(mmyData.modelName || "");
    setShowDomainDropdown(false);
    setAddMakeModelModalOpen(true);
    try {
      const [domainsData, yearsData] = await Promise.all([
       getEditFetchCall(isDomainApprover), APIManager.getmmyYears(),
      ]);
      setDomains(domainsData);
      setMmyYears(yearsData);
    } catch (err) {
      console.error("Error fetching edit data:", err);
    }
  };

useEffect(() => {
  if(!isDomainApprover){
    return;
  }
  APIManager.getDomainApproverAliasID()
    .then((aliasId) => {
      console.log("Fetched domain approver alias ID:", aliasId);
      setDomainDA(aliasId.domainAliasId);
    })
    .catch((err) => {
      console.error("Error fetching domain approver alias ID:", err);
    });
  }, [refreshData]);

console.log("domainDA in global admin categories:", domainDA);
  const handleSaveMMY = () => {
    const { isDomainValid, isMakeValid, isModelValid, isYearValid, allValid } = validateMMYForm(makeModelForm, isDomainApprover);

    // Set error states
    setDomainError(!isDomainValid);
    setMakeError(!isMakeValid);
    setModelError(!isModelValid);
    setYearError(!isYearValid);

    if (!allValid) {
      return;
    }

    const { manufacturerId, payload, modelId } = buildSavePayload(
      makeModelForm, makeMyModel, domains, isDomainApprover, domainDA, isEditMode, editingMMYData
    );
    APIManager.saveMakeModelYear(manufacturerId, payload, modelId)
      .then((response) => {
        // Trigger refresh of all data after successful save
        setRefreshData((prev) => prev + 1);
        return response;
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        setComponentName("appError");
        setErrorMessage(error.message);
        setHeaderMessage(getErrorHeader(error.message));
        setOpenErrorPopup(true);
      });

    // Close modal and reset form
    setAddMakeModelModalOpen(false);
    setMakeModelForm({
      make: "",
      model: "",
      year: [],
      domain: "",
    });
    setDomainError(false);
    setMakeError(false);
    setModelError(false);
    setYearError(false);
    setShowDomainDropdown(true);
  };
  
  useEffect(() => {
    if(isDomainApprover){
      return;
    }
    const fetchDomains = async () => {
      try {
        const domainsData = await APIManager.getAdminDomain();
        setDomains(domainsData || []);
      } catch (error) {
        console.error("Failed to fetch domains:", error);
      }
    };

    fetchDomains();
  }, [refreshData]);

  useEffect(() => {
    setLoader(true);
    APIManager.getAdminOEM()
      .then((json) => {
        const makeMyModelData = json.map(buildManufacturerGroup);
        setMakeMyModel(makeMyModelData);
        setMakeMyModelDomain(makeMyModelData.map((group) => group.domain).sort());
        setLoader(false);
      })
      .catch((error) => {
        console.error("API retry failed:", error);
        setLoader(false);
      });
  }, [refreshData]);

  useEffect(() => {
    dispatch(fetchGlobalAdminCategoriesDetails());
  }, [dispatch, refreshData]);

  useEffect(() => {
    const section = location.state?.selectedSection;
    if (section) {
      setSelectedSidebarItem(section);
      if (section === "MakeMyModel") {
        setCurrentManufacturerPage(0);
      }
    }
  }, [location.state?.selectedSection]);

  const getAvailableMakes = () => getAvailableMakesFromData(makeMyModel, domainChecked);

  const getAvailableModels = () => getAvailableModelsFromData(makeMyModel, domainChecked, makeChecked);

  // Determine which data to display (filtered or all)
  const displayedMakeMyModel = getDisplayedData(filteredMakeMyModel, makeMyModel);

  // Sorted Make Model Year data for current page
  const sortedMmyModels = getSortedModels(displayedMakeMyModel, currentManufacturerPage, mmySortOrder);

  const showMakeMyModel = shouldShowMakeMyModel(isDomainApprover, selectedSidebarItem);
  const showFilterDrawer = showMakeMyModel && filterDrawerOpen;

  return (
    <>
      {renderConditional(!isDomainApprover, <Topheader />)}
      {renderConditional(loader,
        <Backdrop
          sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
          open={loader}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      {renderConditional(!isDomainApprover, <Toolbar />)}

      <Box
        sx={{
          display: "flex",
          width: "100%",
        }}
      >
        {/* Sidebar */}
        {renderConditional(!isDomainApprover, <GlobalAdminSidebar />)}

        {/* Main Content */}
        <Box
          sx={{
            marginLeft: getContentMarginLeft(isDomainApprover),
            flex: 1,
            padding: "16px 32px",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: "860px",
            }}
          >
            {selectedSidebarItem === "Categories" && (
              <Box
                sx={{
                  width: "100%",
                  maxWidth: "860px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: "24px",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: "24px",
                        lineHeight: "24px",
                        color: "#121415",
                      }}
                    >
                      Categories
                    </Typography>
                    <Button
                      onClick={() =>
                        navigate(getAddCategoryPath(isDomainApprover))
                      }
                      variant="contained"
                      sx={{
                        height: "28px",
                        minWidth: "100px",
                        padding: "6px 10px",
                        textTransform: "none",
                        borderRadius: "4px",
                        color: "#FFFFFF",
                        backgroundColor: "#0476E2",
                        fontSize: "13px",
                      }}
                      disabled={readOnly_GlobalAdmin}
                    >
                      Add Category
                    </Button>
                  </Box>

                  <TableContainer
                    sx={{
                      overflow: "auto",
                      width: "100%",
                      maxHeight: "420px",
                      border: "1px solid #E0E0E0",
                      borderRadius: "4px",
                    }}
                  >
                    <Table
                      sx={{
                        "& .MuiTableCell-root": {
                          borderBottom: "1px solid #E0E0E0",
                          borderRight: "1px solid #E0E0E0",
                        },
                        "& .MuiTableCell-root:last-child": {
                          borderRight: "none",
                        },
                        "& .MuiTableRow-root:last-child .MuiTableCell-root": {
                          borderBottom: "none",
                        },
                        "& .MuiTableHead-root .MuiTableCell-root": {
                          borderRight: "none",
                        },
                      }}
                    >
                      <TableHead
                        sx={{
                          position: "sticky",
                          top: 0,
                          zIndex: 1,
                        }}
                      >
                        <TableRow
                          sx={{
                            backgroundColor: "#E8E8E8",
                          }}
                        >
                          <TableCell sx={{ width: "52px" }}>
                            <StyledHeaderCellTypo></StyledHeaderCellTypo>
                          </TableCell>
                          <TableCell
                            sx={{
                              width: "45%",
                              cursor: "pointer",
                              userSelect: "none",
                            }}
                            onClick={handleCategorySortToggle}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <StyledHeaderCellTypo>Name</StyledHeaderCellTypo>
                              <SortIcon sortOrder={categorySortOrder} />
                            </Box>
                          </TableCell>
                          <TableCell sx={{ width: "auto" }}>
                            <StyledHeaderCellTypo>Domain</StyledHeaderCellTypo>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedCategories?.map((data, index) => (
                          <TableRow
                            key={data.id}
                            sx={{
                              height: "48px",
                            }}
                          >
                            <TableCell
                              sx={{
                                padding: "6px 12px",
                                verticalAlign: "middle",
                                width: "52px",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                {getCategoryIconSrc(data.icon) ? (
                                  <img
                                    src={getCategoryIconSrc(data.icon)}
                                    alt="Category Icon"
                                    width="32"
                                    height="32"
                                    style={{
                                      objectFit: "contain",
                                      borderRadius: "4px",
                                    }}
                                  />
                                ) : null}
                              </Box>
                            </TableCell>
                            <TableCell
                              onClick={() =>
                                navigate(
                                  getCategoryDetailPath(isDomainApprover, data.id)
                                )
                              }
                              sx={{
                                padding: "6px 12px",
                                cursor: "pointer",
                                verticalAlign: "middle",
                              }}
                            >
                              <StyledBodyCellTypo
                                sx={{
                                  lineHeight: "16px",
                                  fontSize: "14px",
                                  maxWidth: "200px",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {data.translatedNames["en-US"]}
                              </StyledBodyCellTypo>
                            </TableCell>
                            <TableCell
                              sx={{
                                padding: "6px 12px",
                                verticalAlign: "middle",
                              }}
                            >
                              <StyledBodyCellTypo
                                sx={{
                                  lineHeight: "16px",
                                  fontSize: "14px",
                                }}
                              >
                                {data.domain}
                              </StyledBodyCellTypo>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>
            )}

            {renderConditional(showMakeMyModel,
              <Box
                sx={{
                  width: "100%",
                  maxWidth: "860px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: "24px",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: "20px",
                        lineHeight: "24px",
                        color: "#2A2C2D",
                      }}
                    >
                      Make Model Year
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <IconButton
                        aria-label="filter"
                        size="small"
                        sx={{
                          border: "2px solid #12141599",
                          borderRadius: "2px",
                          padding: "2px",
                          background: "#fff",
                          color: "#12141599",
                          "&:hover": {
                            background: "#E3F2FD",
                            borderColor: "#12141599",
                          },
                        }}
                        onClick={() => setFilterDrawerOpen(true)}
                      >
                        <FilterAltOutlinedIcon />
                      </IconButton>
                      {/* Drawer Filter for Make My Model */}
                      {renderConditional(showFilterDrawer,
                          <Box
                            sx={{
                              position: "fixed",
                              top: 0,
                              right: 0,
                              height: "100vh",
                              width: 340,
                              bgcolor: "background.paper",
                              boxShadow: 3,
                              zIndex: 1300,
                              display: "flex",
                              flexDirection: "column",
                            }}
                          >
                            {/* Sticky Header */}
                            <Box
                              sx={{
                                position: "sticky",
                                top: 0,
                                background: "#fafafa",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                p: 2,
                                borderBottom: "1px solid #eee",
                                zIndex: 2,
                              }}
                            >
                              <Typography variant="h6">Filter</Typography>
                              <IconButton
                                onClick={() => {
                                  setFilterDrawerOpen(false);
                                }}
                              >
                                <CloseIcon />
                              </IconButton>
                            </Box>
                            {/* Scrollable Content */}
                            <Box
                              sx={{
                                flex: 1,
                                overflowY: "auto",
                                p: 2,
                                minHeight: 0,
                                display: "flex",
                                flexDirection: "column",
                              }}
                            >
                              {/* Domain Dropdown with icon and toggle */}
                              <Box sx={{ mb: 2 }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    cursor: "pointer",
                                    mb: 1,
                                  }}
                                  onClick={() =>
                                    setDomainDropdownOpen((open) => !open)
                                  }
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <ArrowDropDownIcon
                                      sx={{
                                        mr: 1,
                                        transform: domainDropdownOpen
                                          ? "rotate(180deg)"
                                          : "rotate(0deg)",
                                        transition: "transform 0.3s ease",
                                      }}
                                    />
                                    <Typography sx={{ fontWeight: 500 }}>
                                      Domain
                                    </Typography>
                                  </Box>
                                  <Typography
                                    sx={{ fontSize: "14px", color: "#666" }}
                                  >
                                    {makeMyModelDomain?.length > 0
                                      ? new Set(makeMyModelDomain).size
                                      : 0}
                                  </Typography>
                                </Box>
                              {renderConditional(domainDropdownOpen,
                                  <Box
                                    sx={{
                                      bgcolor: "#fff",
                                      ml: 3,
                                    }}
                                  >
                                    {/* Search Bar and Select All */}
                                    <Box
                                      sx={{
                                        mb: 1,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        p: 1,
                                        bgcolor: "#1214150D",
                                        borderRadius: "4px",
                                      }}
                                    >
                                      <Button
                                        variant="text"
                                        size="small"
                                        onClick={() => {
                                          const allDomains =
                                            makeMyModelDomain?.length > 0
                                              ? [
                                                ...new Set(makeMyModelDomain),
                                              ].filter((domain) =>
                                                domain
                                                  .toLowerCase()
                                                  .includes(
                                                    domainSearch.toLowerCase()
                                                  )
                                              )
                                              : [];
                                          const allSelected =
                                            allDomains.length > 0 &&
                                            allDomains.every((domain) =>
                                              domainChecked.includes(domain)
                                            );
                                          if (allSelected) {
                                            setDomainChecked([]);
                                          } else {
                                            setDomainChecked(allDomains);
                                          }
                                        }}
                                        sx={{
                                          textTransform: "none",
                                          fontSize: "13px",
                                          color: "#0476E2",
                                          minWidth: "auto",
                                          padding: 0,
                                          fontWeight: 500,
                                          whiteSpace: "nowrap",
                                          "&:hover": {
                                            backgroundColor: "transparent",
                                            textDecoration: "underline",
                                          },
                                        }}
                                      >
                                        {(() => {
                                          const allDomains =
                                            makeMyModelDomain?.length > 0
                                              ? [
                                                ...new Set(makeMyModelDomain),
                                              ].filter((domain) =>
                                                domain
                                                  .toLowerCase()
                                                  .includes(
                                                    domainSearch.toLowerCase()
                                                  )
                                              )
                                              : [];
                                          const allSelected =
                                            allDomains.length > 0 &&
                                            allDomains.every((domain) =>
                                              domainChecked.includes(domain)
                                            );
                                          return allSelected
                                            ? "Unselect All"
                                            : "Select All";
                                        })()}
                                      </Button>
                                      <TextField
                                        size="small"
                                        placeholder="Search for Domain"
                                        value={domainSearch}
                                        onChange={(e) =>
                                          setDomainSearch(e.target.value)
                                        }
                                        sx={{
                                          flex: 1,
                                          "& .MuiOutlinedInput-root": {
                                            height: "32px",
                                            fontSize: "13px",
                                            backgroundColor: "#fff",
                                            borderRadius: "4px",
                                          },
                                        }}
                                        slotProps={{
                                          input: {
                                            startAdornment: (
                                              <InputAdornment position="start">
                                                <SearchIcon
                                                  sx={{
                                                    fontSize: 18,
                                                    color: "#999",
                                                  }}
                                                />
                                              </InputAdornment>
                                            ),
                                          },
                                        }}
                                      />
                                    </Box>
                                    {/* Scrollable Options */}
                                    <Box
                                      sx={{
                                        maxHeight: 120,
                                        overflowY: "auto",
                                      }}
                                    >
                                      {makeMyModelDomain?.length > 0
                                        ? [...new Set(makeMyModelDomain)]
                                          .filter((domain) =>
                                            domain
                                              .toLowerCase()
                                              .includes(
                                                domainSearch.toLowerCase()
                                              )
                                          )
                                          .map((domain) => (
                                            <Box
                                              key={domain}
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                py: 0.5,
                                                px: 1,
                                                cursor: "pointer",
                                                bgcolor:
                                                  domainChecked.includes(
                                                    domain
                                                  )
                                                    ? "#E5F3FF"
                                                    : "transparent",
                                                "&:hover": {
                                                  bgcolor:
                                                    domainChecked.includes(
                                                      domain
                                                    )
                                                      ? "#E5F3FF"
                                                      : "#f5f5f5",
                                                },
                                              }}
                                              onClick={() =>
                                                handleDomainToggle(domain)
                                              }
                                            >
                                              <Checkbox
                                                size="small"
                                                checked={domainChecked.includes(
                                                  domain
                                                )}
                                                sx={{
                                                  padding: "4px",
                                                  "& .MuiSvgIcon-root": {
                                                    fontSize: 18,
                                                  },
                                                  color:
                                                    domainChecked.includes(
                                                      domain
                                                    )
                                                      ? "#0476E2"
                                                      : undefined,
                                                  "&.Mui-checked": {
                                                    color: "#0476E2",
                                                  },
                                                }}
                                              />
                                              <Typography
                                                sx={{
                                                  fontSize: "13px",
                                                  color:
                                                    domainChecked.includes(
                                                      domain
                                                    )
                                                      ? "#0476E2"
                                                      : "#121415",
                                                  fontWeight:
                                                    domainChecked.includes(
                                                      domain
                                                    )
                                                      ? 500
                                                      : 400,
                                                }}
                                              >
                                                {domain}
                                              </Typography>
                                            </Box>
                                          ))
                                        : ["Domain 1", "Domain 2", "Domain 3"]
                                          .filter((domain) =>
                                            domain
                                              .toLowerCase()
                                              .includes(
                                                domainSearch.toLowerCase()
                                              )
                                          )
                                          .map((domain) => (
                                            <Box
                                              key={domain}
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                py: 1,
                                                px: 2,
                                                cursor: "pointer",
                                                bgcolor:
                                                  domainChecked.includes(
                                                    domain
                                                  )
                                                    ? "#E5F3FF"
                                                    : "transparent",
                                                "&:hover": {
                                                  bgcolor:
                                                    domainChecked.includes(
                                                      domain
                                                    )
                                                      ? "#E5F3FF"
                                                      : "#f5f5f5",
                                                },
                                              }}
                                              onClick={() =>
                                                handleDomainToggle(domain)
                                              }
                                            >
                                              <Checkbox
                                                size="small"
                                                checked={domainChecked.includes(
                                                  domain
                                                )}
                                                sx={{
                                                  color:
                                                    domainChecked.includes(
                                                      domain
                                                    )
                                                      ? "#0476E2"
                                                      : undefined,
                                                  "&.Mui-checked": {
                                                    color: "#0476E2",
                                                  },
                                                }}
                                              />
                                              <Typography
                                                sx={{
                                                  color:
                                                    domainChecked.includes(
                                                      domain
                                                    )
                                                      ? "#0476E2"
                                                      : "#121415",
                                                  fontWeight:
                                                    domainChecked.includes(
                                                      domain
                                                    )
                                                      ? 500
                                                      : 400,
                                                }}
                                              >
                                                {domain}
                                              </Typography>
                                            </Box>
                                          ))}
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                              {/* Make Dropdown with icon and toggle */}
                              <Box sx={{ mb: 2 }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    cursor:
                                      domainChecked.length > 0
                                        ? "pointer"
                                        : "not-allowed",
                                    mb: 1,
                                    opacity: domainChecked.length > 0 ? 1 : 0.5,
                                  }}
                                  onClick={() => {
                                    if (domainChecked.length > 0) {
                                      setMakeDropdownOpen((open) => !open);
                                    }
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <ArrowDropDownIcon
                                      sx={{
                                        mr: 1,
                                        transform: makeDropdownOpen
                                          ? "rotate(180deg)"
                                          : "rotate(0deg)",
                                        transition: "transform 0.3s ease",
                                      }}
                                    />
                                    <Typography sx={{ fontWeight: 500 }}>
                                      Make
                                    </Typography>
                                  </Box>
                                  <Typography
                                    sx={{ fontSize: "14px", color: "#666" }}
                                  >
                                    {getAvailableMakes().length}
                                  </Typography>
                                </Box>
                                {makeDropdownOpen &&
                                  domainChecked.length > 0 && (
                                    <Box
                                      sx={{
                                        bgcolor: "#fff",
                                        ml: 3,
                                      }}
                                    >
                                      {/* Search Bar and Select All */}
                                      <Box
                                        sx={{
                                          mb: 1,
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1,
                                          p: 1,
                                          bgcolor: "#1214150D",
                                          borderRadius: "4px",
                                        }}
                                      >
                                        <Button
                                          variant="text"
                                          size="small"
                                          onClick={() => {
                                            const allMakes =
                                              getAvailableMakes().filter(
                                                (make) =>
                                                  make
                                                    ?.toLowerCase()
                                                    .includes(
                                                      makeSearch.toLowerCase()
                                                    )
                                              );
                                            const allSelected =
                                              allMakes.length > 0 &&
                                              allMakes.every((make) =>
                                                makeChecked.includes(make)
                                              );
                                            if (allSelected) {
                                              setMakeChecked([]);
                                            } else {
                                              setMakeChecked(allMakes);
                                            }
                                          }}
                                          sx={{
                                            textTransform: "none",
                                            fontSize: "13px",
                                            color: "#0476E2",
                                            minWidth: "auto",
                                            padding: 0,
                                            fontWeight: 500,
                                            whiteSpace: "nowrap",
                                            "&:hover": {
                                              backgroundColor: "transparent",
                                              textDecoration: "underline",
                                            },
                                          }}
                                        >
                                          {(() => {
                                            const allMakes =
                                              getAvailableMakes().filter(
                                                (make) =>
                                                  make
                                                    ?.toLowerCase()
                                                    .includes(
                                                      makeSearch.toLowerCase()
                                                    )
                                              );
                                            const allSelected =
                                              allMakes.length > 0 &&
                                              allMakes.every((make) =>
                                                makeChecked.includes(make)
                                              );
                                            return allSelected
                                              ? "Unselect All"
                                              : "Select All";
                                          })()}
                                        </Button>
                                        <TextField
                                          size="small"
                                          placeholder="Search for Make"
                                          value={makeSearch}
                                          onChange={(e) =>
                                            setMakeSearch(e.target.value)
                                          }
                                          sx={{
                                            flex: 1,
                                            "& .MuiOutlinedInput-root": {
                                              height: "32px",
                                              fontSize: "13px",
                                              backgroundColor: "#fff",
                                              borderRadius: "4px",
                                            },
                                          }}
                                          slotProps={{
                                            input: {
                                              startAdornment: (
                                                <InputAdornment position="start">
                                                  <SearchIcon
                                                    sx={{
                                                      fontSize: 18,
                                                      color: "#999",
                                                    }}
                                                  />
                                                </InputAdornment>
                                              ),
                                            },
                                          }}
                                        />
                                      </Box>
                                      {/* Scrollable Options */}
                                      <Box
                                        sx={{
                                          maxHeight: 120,
                                          overflowY: "auto",
                                        }}
                                      >
                                        {getAvailableMakes()
                                          .filter((make) =>
                                            make
                                              ?.toLowerCase()
                                              .includes(
                                                makeSearch.toLowerCase()
                                              )
                                          )
                                          .map((make) => {
                                            const isChecked =
                                              makeChecked.includes(make);
                                            return (
                                              <Box
                                                key={make}
                                                sx={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  py: 0.5,
                                                  px: 1,
                                                  cursor: "pointer",
                                                  bgcolor: isChecked
                                                    ? "#E5F3FF"
                                                    : "transparent",
                                                  "&:hover": {
                                                    bgcolor: isChecked
                                                      ? "#E5F3FF"
                                                      : "#f5f5f5",
                                                  },
                                                }}
                                                onClick={() =>
                                                  handleMakeToggle(make)
                                                }
                                              >
                                                <Checkbox
                                                  size="small"
                                                  checked={isChecked}
                                                  sx={{
                                                    padding: "4px",
                                                    "& .MuiSvgIcon-root": {
                                                      fontSize: 18,
                                                    },
                                                    color: isChecked
                                                      ? "#0476E2"
                                                      : undefined,
                                                    "&.Mui-checked": {
                                                      color: "#0476E2",
                                                    },
                                                  }}
                                                />
                                                <Typography
                                                  sx={{
                                                    fontSize: "13px",
                                                    color: isChecked
                                                      ? "#0476E2"
                                                      : "#121415",
                                                    fontWeight: isChecked
                                                      ? 500
                                                      : 400,
                                                  }}
                                                >
                                                  {make}
                                                </Typography>
                                              </Box>
                                            );
                                          })}
                                      </Box>
                                    </Box>
                                  )}
                              </Box>
                              {/* Model Dropdown with icon and toggle */}
                              <Box sx={{ mb: 2 }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    cursor:
                                      makeChecked.length > 0
                                        ? "pointer"
                                        : "not-allowed",
                                    mb: 1,
                                    opacity: makeChecked.length > 0 ? 1 : 0.5,
                                  }}
                                  onClick={() => {
                                    if (makeChecked.length > 0) {
                                      setModelDropdownOpen((open) => !open);
                                    }
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <ArrowDropDownIcon
                                      sx={{
                                        mr: 1,
                                        transform: modelDropdownOpen
                                          ? "rotate(180deg)"
                                          : "rotate(0deg)",
                                        transition: "transform 0.3s ease",
                                      }}
                                    />
                                    <Typography sx={{ fontWeight: 500 }}>
                                      Model
                                    </Typography>
                                  </Box>
                                  <Typography
                                    sx={{ fontSize: "14px", color: "#666" }}
                                  >
                                    {getAvailableModels().length}
                                  </Typography>
                                </Box>
                                {modelDropdownOpen &&
                                  makeChecked.length > 0 && (
                                    <Box
                                      sx={{
                                        bgcolor: "#fff",
                                        ml: 3,
                                      }}
                                    >
                                      {/* Search Bar and Select All */}
                                      <Box
                                        sx={{
                                          mb: 1,
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1,
                                          p: 1,
                                          bgcolor: "#1214150D",
                                          borderRadius: "4px",
                                        }}
                                      >
                                        <Button
                                          variant="text"
                                          size="small"
                                          onClick={() => {
                                            const allModels =
                                              getAvailableModels().filter(
                                                (model) =>
                                                  model
                                                    ?.toLowerCase()
                                                    .includes(
                                                      modelSearch.toLowerCase()
                                                    )
                                              );
                                            const allSelected =
                                              allModels.length > 0 &&
                                              allModels.every((model) =>
                                                modelChecked.includes(model)
                                              );
                                            if (allSelected) {
                                              setModelChecked([]);
                                            } else {
                                              setModelChecked(allModels);
                                            }
                                          }}
                                          sx={{
                                            textTransform: "none",
                                            fontSize: "13px",
                                            color: "#0476E2",
                                            minWidth: "auto",
                                            padding: 0,
                                            fontWeight: 500,
                                            whiteSpace: "nowrap",
                                            "&:hover": {
                                              backgroundColor: "transparent",
                                              textDecoration: "underline",
                                            },
                                          }}
                                        >
                                          {(() => {
                                            const allModels =
                                              getAvailableModels().filter(
                                                (model) =>
                                                  model
                                                    ?.toLowerCase()
                                                    .includes(
                                                      modelSearch.toLowerCase()
                                                    )
                                              );
                                            const allSelected =
                                              allModels.length > 0 &&
                                              allModels.every((model) =>
                                                modelChecked.includes(model)
                                              );
                                            return allSelected
                                              ? "Unselect All"
                                              : "Select All";
                                          })()}
                                        </Button>
                                        <TextField
                                          size="small"
                                          placeholder="Search for Model"
                                          value={modelSearch}
                                          onChange={(e) =>
                                            setModelSearch(e.target.value)
                                          }
                                          sx={{
                                            flex: 1,
                                            "& .MuiOutlinedInput-root": {
                                              height: "32px",
                                              fontSize: "13px",
                                              backgroundColor: "#fff",
                                              borderRadius: "4px",
                                            },
                                          }}
                                          slotProps={{
                                            input: {
                                              startAdornment: (
                                                <InputAdornment position="start">
                                                  <SearchIcon
                                                    sx={{
                                                      fontSize: 18,
                                                      color: "#999",
                                                    }}
                                                  />
                                                </InputAdornment>
                                              ),
                                            },
                                          }}
                                        />
                                      </Box>
                                      {/* Scrollable Options */}
                                      <Box
                                        sx={{
                                          maxHeight: 120,
                                          overflowY: "auto",
                                        }}
                                      >
                                        {getAvailableModels()
                                          .filter((model) =>
                                            model
                                              ?.toLowerCase()
                                              .includes(
                                                modelSearch.toLowerCase()
                                              )
                                          )
                                          .map((model) => {
                                            const isChecked =
                                              modelChecked.includes(model);
                                            return (
                                              <Box
                                                key={model}
                                                sx={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  py: 0.5,
                                                  px: 1,
                                                  cursor: "pointer",
                                                  bgcolor: isChecked
                                                    ? "#E5F3FF"
                                                    : "transparent",
                                                  "&:hover": {
                                                    bgcolor: isChecked
                                                      ? "#E5F3FF"
                                                      : "#f5f5f5",
                                                  },
                                                }}
                                                onClick={() =>
                                                  handleModelToggle(model)
                                                }
                                              >
                                                <Checkbox
                                                  size="small"
                                                  checked={isChecked}
                                                  sx={{
                                                    padding: "4px",
                                                    "& .MuiSvgIcon-root": {
                                                      fontSize: 18,
                                                    },
                                                    color: isChecked
                                                      ? "#0476E2"
                                                      : undefined,
                                                    "&.Mui-checked": {
                                                      color: "#0476E2",
                                                    },
                                                  }}
                                                />
                                                <Typography
                                                  sx={{
                                                    fontSize: "13px",
                                                    color: isChecked
                                                      ? "#0476E2"
                                                      : "#121415",
                                                    fontWeight: isChecked
                                                      ? 500
                                                      : 400,
                                                  }}
                                                >
                                                  {model}
                                                </Typography>
                                              </Box>
                                            );
                                          })}
                                      </Box>
                                    </Box>
                                  )}
                              </Box>
                            </Box>
                            {/* Sticky Footer */}
                            <Box
                              sx={{
                                position: "sticky",
                                bottom: 0,
                                background: "#fff",
                                borderTop: "1px solid #eee",
                                p: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                zIndex: 2,
                              }}
                            >
                              {/* Left: Reset to Default */}
                              <Button
                                variant="text"
                                color="inherit"
                                onClick={handleResetFilter}
                                sx={{
                                  maxWidth: "125px",
                                  whiteSpace: "nowrap",
                                  fontWeight: 400,
                                  textTransform: "none",
                                }}
                              >
                                Reset to Default
                              </Button>
                              {/* Right: Cancel and Apply */}
                              <Box sx={{ display: "flex", gap: 1 }}>
                                <Button
                                  variant="outlined"
                                  color="primary"
                                  onClick={handleCancelFilter}
                                  sx={{
                                    whiteSpace: "nowrap",
                                    fontWeight: 400,
                                    textTransform: "none",
                                  }}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  variant="contained"
                                  color="primary"
                                  onClick={handleApplyFilter}
                                  sx={{
                                    whiteSpace: "nowrap",
                                    fontWeight: 400,
                                    textTransform: "none",
                                  }}
                                >
                                  Apply
                                </Button>
                              </Box>
                            </Box>
                          </Box>
                        )}
                      <Button
                        onClick={handleAddMMY}
                        variant="contained"
                        sx={{
                          height: "32px",
                          minWidth: "166px",
                          padding: "10px 14px",
                          textTransform: "none",
                          borderRadius: "4px",
                          color: "#FFFFFF",
                          backgroundColor: "#0476E2",
                          whiteSpace: "nowrap",
                        }}
                        disabled={readOnly_GlobalAdmin}
                      >
                        Add Make Model Year
                      </Button>
                    </Box>
                  </Box>

                  {/* Add Make Model Year Modal */}
                  <Dialog
                    open={addMakeModelModalOpen}
                    onClose={() => {
                      setAddMakeModelModalOpen(false);
                      // Reset form values when modal closes
                      setMakeModelForm({
                        make: "",
                        model: "",
                        year: [],
                        domain: "",
                      });
                      setMakeInputValue("");
                      setModelInputValue("");
                      setModelError(false);
                      setDomainError(false);
                      setMakeError(false);
                      setYearError(false);
                      setShowDomainDropdown(true); // Reset to show dropdown
                    }}
                    maxWidth="sm"
                    fullWidth
                    slotProps={{
                      paper: {
                        sx: {
                          borderRadius: "8px",
                          padding: "8px",
                        },
                      },
                    }}
                  >
                    <DialogTitle
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "16px 24px",
                        fontSize: "20px",
                        fontWeight: 500,
                      }}
                    >
                      Add Make Model Year
                      <IconButton
                        onClick={() => {
                          setAddMakeModelModalOpen(false);
                          // Reset form values when modal closes
                          setMakeModelForm({
                            make: "",
                            model: "",
                            year: [],
                            domain: "",
                          });
                          setMakeInputValue("");
                          setModelInputValue("");
                          setModelError(false);
                          setDomainError(false);
                          setMakeError(false);
                          setYearError(false);
                          setShowDomainDropdown(true); // Reset to show dropdown
                        }}
                        sx={{ padding: "4px" }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </DialogTitle>
                    <DialogContent sx={{ padding: "0 24px 24px" }}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        {/* Make, Model, Year Fields in a Row */}
                        <Box sx={{ display: "flex", gap: 2 }}>
                          {/* Make Field */}
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              sx={{
                                fontSize: "14px",
                                fontWeight: 400,
                                marginBottom: "8px",
                                color: "#121415",
                              }}
                            >
                              Make <span style={{ color: "#D32F2F" }}>*</span>
                            </Typography>
                            <Autocomplete
                              freeSolo
                              fullWidth
                              autoSelect={false}
                              autoHighlight={false}
                              disableClearable={true}
                              forcePopupIcon={true}
                              popupIcon={<ArrowDropDownIcon />}
                              disabled={isEditMode}
                              value={makeModelForm.make}
                              inputValue={makeInputValue}
                              onChange={(event, newValue) => {
                                // Check if selected make exists in makeMyModel
                                const existingMake = makeMyModel.find(
                                  (group) => group.manufacturerName === newValue
                                );

                                if (existingMake) {
                                  // Auto-populate domain from existing make
                                  setMakeModelForm({
                                    ...makeModelForm,
                                    make: newValue || "",
                                    domain: existingMake.domain,
                                    model: "", // Reset model when make changes
                                  });
                                  setShowDomainDropdown(false); // Hide dropdown, show chip (non-editable)
                                  if (domainError) {
                                    setDomainError(false);
                                  }
                                } else {
                                  // New make - keep domain empty, show dropdown
                                  setMakeModelForm({
                                    ...makeModelForm,
                                    make: newValue || "",
                                    domain: "",
                                    model: "", // Reset model when make changes
                                  });
                                  setShowDomainDropdown(true); // Show dropdown for new make
                                }

                                setMakeInputValue(newValue || "");
                                // Clear model input when make changes
                                setModelInputValue("");
                                // Clear error when user selects a value
                                if (makeError && newValue) {
                                  setMakeError(false);
                                }
                              }}
                              onInputChange={(event, newInputValue, reason) => {
                                setMakeInputValue(newInputValue);
                                if (reason === "input") {
                                  const existingMake = makeMyModel.find(
                                    (group) => group.manufacturerName === newInputValue
                                  );
                                  
                                  if (existingMake) {
                                    setMakeModelForm({
                                      ...makeModelForm,
                                      make: newInputValue || "",
                                      domain: existingMake.domain,
                                      model: "",
                                    });
                                    setShowDomainDropdown(false);
                                    if (domainError) {
                                      setDomainError(false);
                                    }
                                  } else {
                                    setMakeModelForm({
                                      ...makeModelForm,
                                      make: newInputValue || "",
                                      domain: "",
                                      model: "",
                                    });
                                    setShowDomainDropdown(true);
                                  }
                                  // Clear model input when make changes
                                  setModelInputValue("");
                                } else if (
                                  reason === "clear" ||
                                  reason === "reset"
                                ) {
                                  // If reason is 'clear' or 'reset', clear everything
                                  setMakeModelForm({
                                    ...makeModelForm,
                                    make: "",
                                    domain: "",
                                    model: "",
                                  });
                                  setModelInputValue("");
                                  setShowDomainDropdown(true);
                                }
                                // Clear error when user types
                                if (makeError && newInputValue) {
                                  setMakeError(false);
                                }
                              }}
                              options={makeMyModel.map(
                                (group) => group.manufacturerName
                              )}
                              renderOption={(props, option) => (
                                <li {...props} key={option}>
                                  <Radio
                                    checked={makeModelForm.make === option}
                                    sx={{
                                      padding: "0 8px 0 0",
                                      "& .MuiSvgIcon-root": {
                                        fontSize: 18,
                                      },
                                    }}
                                  />
                                  {option}
                                </li>
                              )}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Eg: Asterix"
                                  error={makeError}
                                  helperText={
                                    makeError ? "Make is required" : ""
                                  }
                                  sx={{
                                    "& .MuiInputBase-root": {
                                      height: "40px",
                                      fontSize: "14px",
                                    },
                                    "& .MuiInputBase-input": {
                                      padding: "8px 14px !important",
                                    },
                                    "& .MuiInputBase-input::placeholder": {
                                      color: "#717273",
                                      opacity: 1,
                                    },
                                  }}
                                />
                              )}
                              slotProps={{
                                listbox: {
                                  sx: {
                                    maxHeight: 200,
                                    "& .MuiAutocomplete-option": {
                                      minHeight: "32px",
                                      fontSize: "14px",
                                      padding: "4px 14px",
                                    },
                                  },
                                },
                              }}
                            />
                          </Box>

                          <Box sx={{ flex: 1 }}>
                            <Typography
                              sx={{
                                fontSize: "14px",
                                fontWeight: 400,
                                marginBottom: "8px",
                                color: "#121415",
                              }}
                            >
                              Model <span style={{ color: "#D32F2F" }}>*</span>
                            </Typography>
                            <Autocomplete
                              freeSolo
                              fullWidth
                              autoSelect={false}
                              autoHighlight={false}
                              disableClearable={true}
                              forcePopupIcon={true}
                              popupIcon={<ArrowDropDownIcon />}
                              disabled={!makeModelForm.make || isEditMode}
                              value={makeModelForm.model}
                              inputValue={modelInputValue}
                              onChange={(event, newValue) => {
                                setMakeModelForm({
                                  ...makeModelForm,
                                  model: newValue || "",
                                });
                                setModelInputValue(newValue || "");
                                // Clear error when user selects a value
                                if (modelError && newValue) {
                                  setModelError(false);
                                }
                              }}
                              onInputChange={(event, newInputValue, reason) => {
                                // Update input value for display
                                setModelInputValue(newInputValue);
                                // Also update form value for custom/new Model names
                                if (reason === "input") {
                                  // When user types, update the form value too
                                  setMakeModelForm({
                                    ...makeModelForm,
                                    model: newInputValue || "",
                                  });
                                } else if (
                                  reason === "clear" ||
                                  reason === "reset"
                                ) {
                                  // If reason is 'clear' or 'reset', clear the model
                                  setMakeModelForm({
                                    ...makeModelForm,
                                    model: "",
                                  });
                                }
                                // Clear error when user types
                                if (modelError && newInputValue) {
                                  setModelError(false);
                                }
                              }}
                              options={
                                makeModelForm.make
                                  ? (() => {
                                    const makeGroup = makeMyModel.find(
                                      (group) =>
                                        group.manufacturerName ===
                                        makeModelForm.make
                                    );
                                    if (makeGroup) {
                                      return makeGroup.models
                                        .map((model) => model.modelName)
                                        .filter(
                                          (value, index, self) =>
                                            self.indexOf(value) === index
                                        ); 
                                    }
                                    return [];
                                  })()
                                  : []
                              }
                              renderOption={(props, option) => (
                                <li {...props} key={option}>
                                  <Radio
                                    checked={makeModelForm.model === option}
                                    sx={{
                                      padding: "0 8px 0 0",
                                      "& .MuiSvgIcon-root": {
                                        fontSize: 18,
                                      },
                                    }}
                                  />
                                  {option}
                                </li>
                              )}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Eg: Lorem Ipsum"
                                  error={modelError}
                                  helperText={
                                    modelError ? "Model is required" : ""
                                  }
                                  sx={{
                                    "& .MuiInputBase-root": {
                                      height: "40px",
                                      fontSize: "14px",
                                    },
                                    "& .MuiInputBase-input": {
                                      padding: "8px 14px !important",
                                    },
                                    "& .MuiInputBase-input::placeholder": {
                                      color: "#717273",
                                      opacity: 1,
                                    },
                                  }}
                                />
                              )}
                              slotProps={{
                                listbox: {
                                  sx: {
                                    maxHeight: 200,
                                    "& .MuiAutocomplete-option": {
                                      minHeight: "32px",
                                      fontSize: "14px",
                                      padding: "4px 14px",
                                    },
                                  },
                                },
                              }}
                            />
                          </Box>

                          {/* Year Field */}
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              sx={{
                                fontSize: "14px",
                                fontWeight: 400,
                                marginBottom: "8px",
                                color: "#121415",
                              }}
                            >
                              Year <span style={{ color: "#D32F2F" }}>*</span>
                            </Typography>
                            <Select
                              fullWidth
                              displayEmpty
                              multiple
                              value={makeModelForm.year}
                              onChange={(e) => {
                                const value = e.target.value;
                                setMakeModelForm({
                                  ...makeModelForm,
                                  year:
                                    typeof value === "string"
                                      ? value.split(",")
                                      : value,
                                });
                                // Clear error when user selects a value
                                if (yearError && value.length > 0) {
                                  setYearError(false);
                                }
                              }}
                              error={yearError}
                              renderValue={(selected) => {
                                if (!selected || selected.length === 0) {
                                  return (
                                    <span style={{ color: "#717273" }}>
                                      Eg: 2025
                                    </span>
                                  );
                                }
                                const displayYears = selected.slice(0, 3);
                                return displayYears.join(", ");
                              }}
                              sx={{
                                height: "40px",
                                fontSize: "14px",
                                "& .MuiSelect-select": {
                                  padding: "8px 14px",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                },
                              }}
                              MenuProps={{
                                slotProps: {
                                  paper: {
                                    sx: {
                                      maxHeight: 200,
                                      "& .MuiMenuItem-root": {
                                        minHeight: "32px",
                                        fontSize: "14px",
                                        padding: "4px 14px",
                                      },
                                    },
                                  },
                                },
                              }}
                            >
                              <MenuItem value="" disabled>
                                Eg: 2025
                              </MenuItem>
                              {mmyYears?.length > 0 &&
                                mmyYears.map((year) => (
                                  <MenuItem key={year} value={year}>
                                    <Checkbox
                                      checked={
                                        makeModelForm.year.includes(year)
                                      }
                                      sx={{
                                        padding: "0 8px 0 0",
                                        "& .MuiSvgIcon-root": {
                                          fontSize: 18,
                                        },
                                      }}
                                    />
                                    {year}
                                  </MenuItem>
                                ))}
                            </Select>
                            {renderConditional(yearError,
                              <Typography
                                sx={{
                                  color: "#d32f2f",
                                  fontSize: "0.75rem",
                                  marginTop: "4px",
                                  marginLeft: "14px",
                                }}
                              >
                                Year is required
                              </Typography>
                            )}
                          </Box>
                        </Box>

                        {/* Domain Field */}
                       {renderConditional(!isDomainApprover,
                       <Box>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 500,
                              marginBottom: "4px",
                              color: "#121415",
                            }}
                          >
                            Domain <span style={{ color: "#D32F2F" }}>*</span>
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: 400,
                              marginBottom: "8px",
                              color: "#666666",
                            }}
                          >
                            Defines if a Make Model should belong to a particular OEM.
                          </Typography>

                          {/* Show chip if domain is selected and dropdown is hidden */}
                          {!showDomainDropdown && makeModelForm.domain ? (
                            <Chip
                              label={makeModelForm.domain}
                              onClick={() => {
                                const existingMake = makeMyModel.find(
                                  (group) => group.manufacturerName === makeModelForm.make
                                );
                                if (!isEditMode && !existingMake) {
                                  setShowDomainDropdown(true);
                                }
                              }}
                              sx={{
                                height: "28px",
                                fontSize: "14px",
                                fontWeight: 400,
                                backgroundColor: "#E8E8E8",
                                color: "#000000DE",
                                border: "1px solid #12141514",
                                borderRadius: "4px",
                                cursor: (() => {
                                  const existingMake = makeMyModel.find(
                                    (group) => group.manufacturerName === makeModelForm.make
                                  );
                                  return isEditMode || existingMake ? "not-allowed" : "pointer";
                                })(),
                                opacity: (() => {
                                  const existingMake = makeMyModel.find(
                                    (group) => group.manufacturerName === makeModelForm.make
                                  );
                                  return isEditMode || existingMake ? 0.6 : 1;
                                })(),
                                "& .MuiChip-label": {
                                  padding: "0 12px",
                                },
                                "&:hover": {
                                  backgroundColor: (() => {
                                    const existingMake = makeMyModel.find(
                                      (group) => group.manufacturerName === makeModelForm.make
                                    );
                                    return isEditMode || existingMake ? "#E8E8E8" : "#D8D8D8";
                                  })(),
                                },
                              }}
                            />
                          ) : (
                            /* Show dropdown if showDomainDropdown is true or domain not selected */
                            <Select
                              fullWidth
                              displayEmpty
                              disabled={isEditMode}
                              value={makeModelForm.domain}
                              onChange={(e) => {
                                setMakeModelForm({
                                  ...makeModelForm,
                                  domain: e.target.value,
                                  // Don't reset make and model - user is associating new make with domain
                                });
                                // Clear error when user selects a value
                                if (domainError && e.target.value) {
                                  setDomainError(false);
                                }
                                // Hide dropdown and show chip after selection
                                if (e.target.value) {
                                  setShowDomainDropdown(false);
                                }
                              }}
                              error={domainError}
                              renderValue={(selected) => {
                                if (!selected) {
                                  return (
                                    <span style={{ color: "#717273" }}>
                                      Select Domain
                                    </span>
                                  );
                                }
                                return selected;
                              }}
                              sx={{
                                height: "40px",
                                fontSize: "14px",
                                "& .MuiSelect-select": {
                                  padding: "8px 14px",
                                },
                              }}
                              MenuProps={{
                                slotProps: {
                                  paper: {
                                    sx: {
                                      maxHeight: 200,
                                      "& .MuiMenuItem-root": {
                                        minHeight: "32px",
                                        fontSize: "14px",
                                        padding: "4px 14px",
                                      },
                                    },
                                  },
                                },
                              }}
                            >
                              <MenuItem value="" disabled>
                                Select Domain
                              </MenuItem>
                              {makeMyModelDomain?.length > 0
                                ? [...new Set(makeMyModelDomain)].map(
                                  (domain) => (
                                    <MenuItem key={domain} value={domain}>
                                      <Checkbox
                                        checked={
                                          makeModelForm.domain === domain
                                        }
                                        sx={{
                                          padding: "0 8px 0 0",
                                          "& .MuiSvgIcon-root": {
                                            fontSize: 18,
                                          },
                                        }}
                                      />
                                      {domain}
                                    </MenuItem>
                                  )
                                )
                                : null}
                            </Select>
                          )}
                          {renderConditional(domainError,
                            <Typography
                              sx={{
                                color: "#d32f2f",
                                fontSize: "0.75rem",
                                marginTop: "4px",
                                marginLeft: "14px",
                              }}
                            >
                              Domain is required
                            </Typography>
                          )}
                        </Box>
                       )}

                        {/* Delete Section - Only show in Edit Mode */}
                        {renderConditional(isEditMode,
                          <Box sx={{ marginTop: "8px" }}>
                            <Typography
                              sx={{
                                fontSize: "14px",
                                fontWeight: 500,
                                marginBottom: "4px",
                                color: "#121415",
                              }}
                            >
                              Delete Make Model year
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "12px",
                                fontWeight: 400,
                                marginBottom: "12px",
                                color: "#666666",
                              }}
                            >
                              This Make Model can not be deleted because it has
                              attached application listings.
                            </Typography>
                            <Button
                              variant="outlined"
                              disabled
                              sx={{
                                textTransform: "none",
                                color: "#EA2519",
                                borderColor: "#EA2519",
                                padding: "8px 24px",
                                fontSize: "14px",
                                fontWeight: 500,
                                height: "36px",
                                "&.Mui-disabled": {
                                  color: "#D32F2F",
                                  borderColor: "#D32F2F",
                                  opacity: 0.5,
                                },
                              }}
                            >
                              Delete
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </DialogContent>
                    <DialogActions
                      sx={{
                        padding: 0,
                        paddingTop: "24px",
                        justifyContent: "flex-end",
                        gap: 1,
                      }}
                    >
                      <Button
                        onClick={() => setAddMakeModelModalOpen(false)}
                        variant="outlined"
                        sx={{
                          textTransform: "none",
                          padding: "8px 24px",
                          fontSize: "14px",
                          fontWeight: 500,
                          height: "36px",
                          color: "#666666",
                          borderColor: "#E0E0E0",
                          backgroundColor: "#F5F5F5",
                          "&:hover": {
                            backgroundColor: "#EEEEEE",
                            borderColor: "#E0E0E0",
                          },
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleSaveMMY}
                        variant="contained"
                        sx={{
                          textTransform: "none",
                          padding: "8px 24px",
                          fontSize: "14px",
                          fontWeight: 500,
                          height: "36px",
                          backgroundColor: "#0476E2",
                          "&:hover": {
                            backgroundColor: "#0356a6",
                          },
                        }}
                      >
                        Save
                      </Button>
                    </DialogActions>
                  </Dialog>

                  <TableContainer
                    sx={{
                      overflow: "auto",
                      width: "100%",
                      maxHeight: "500px",
                      border: "1px solid #E0E0E0",
                      borderRadius: "4px",
                    }}
                  >
                    <Table
                      sx={{
                        "& .MuiTableCell-root": {
                          padding: "12px 16px",
                          borderBottom: "1px solid #E0E0E0",
                        },
                      }}
                    >
                      <TableHead
                        sx={{
                          position: "sticky",
                          top: 0,
                          zIndex: 1,
                        }}
                      >
                        <TableRow
                          sx={{
                            backgroundColor: "#E8E8E8",
                            height: "48px",
                          }}
                        >
                          <TableCell
                            sx={{
                              width: "406px",
                              cursor: "pointer",
                              userSelect: "none",
                            }}
                            onClick={handleMmySortToggle}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 600,
                                  color: "#121415",
                                }}
                              >
                                Name
                              </Typography>
                              <SortIcon sortOrder={mmySortOrder} />
                            </Box>
                          </TableCell>
                          <TableCell sx={{ width: "406px" }}>
                            <Typography
                              sx={{
                                fontSize: "14px",
                                fontWeight: 600,
                                color: "#121415",
                              }}
                            >
                              Domain
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedMmyModels.map((data) => (
                          <TableRow
                            key={data.id}
                            sx={{
                              height: "48px",
                              "&:hover": {
                                backgroundColor: "#F5F5F5",
                              },
                            }}
                          >
                            <TableCell>
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 600,
                                  color: "#121415",
                                }}
                              >
                                {data.name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    color: "#121415",
                                  }}
                                >
                                  {data.domain}
                                </Typography>
                                <MmyRowAction data={data} onEdit={handleEditMMY} readOnly={readOnly_GlobalAdmin} />
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Pagination Controls */}
                  {renderConditional(displayedMakeMyModel.length > 0,
                    <PaginationControls
                      currentPage={currentManufacturerPage}
                      totalPages={displayedMakeMyModel.length}
                      onPageChange={setCurrentManufacturerPage}
                    />
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
         <PopupDialog
                openErrorPopup={openErrorPopup}
                errorMessage={errorMessage}
                headerMessage={headerMessage}
                componentName={componentName}
                onClose={() => setOpenErrorPopup(false)}
              />
      </Box>
    </>
  );
};

SortIcon.propTypes = {
  sortOrder: PropTypes.string,
};

MmyRowAction.propTypes = {
  data: PropTypes.object,
  onEdit: PropTypes.func,
  readOnly: PropTypes.bool,
};

PaginationControls.propTypes = {
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
  onPageChange: PropTypes.func,
};

GlobalAdminCategories.propTypes = {
  isDomainApprover: PropTypes.bool,
  initialSection: PropTypes.string,
};

export default GlobalAdminCategories;
